import {
  fbGetGroup,
  fbGetAdminGroups,
} from "../../services/firebaseService/endPoints/user";
import { fbAuth } from "../../services/firebaseService/connection";
import logger from "../../utils/logger";
import useAuthStore from "../../store/auth/auth";
import useAdminDataStore from "../../store/adminData/adminData";
import useUserStore from "../../store/user/user";

const checkSuperadminStatus = async () => {
  try {
    if (!fbAuth.currentUser) {
      return false;
    }

    const idTokenResult = await fbAuth.currentUser.getIdTokenResult();
    return idTokenResult.claims.superadmin === true;
  } catch (error) {
    console.error("Error checking superadmin status:", error);
    return false;
  }
};

// Core async logic — can be called directly from functional components
// OR wrapped in a thunk for class components.
export const updateUser = async () => {
  try {
    if (fbAuth.currentUser) {
      await fbAuth.currentUser.getIdToken(true);
    }

    const isSuperadmin = await checkSuperadminStatus();

    useAuthStore.getState().setSuperadmin(isSuperadmin);

    if (isSuperadmin) {
      const profile = { ...fbAuth.currentUser };
      useUserStore.getState().setAdmin(profile, {});
    } else {
      const groups = await fbGetAdminGroups();
      let profile = { ...fbAuth.currentUser };

      if (Object.keys(groups).length > 0) {
        let promises = Object.keys(groups).map((group) => fbGetGroup(group));
        const values = await Promise.all(promises);

        useAdminDataStore.getState().setAdminGroups(values);
        useUserStore.getState().setAdmin(profile, groups);
      } else {
        useAuthStore.getState().logout();
        useUserStore.getState().setUnauthorized();
      }
    }
  } catch (err) {
    logger.error(err);
    logger.log("[emailAuth] updateUser threw. code:", err?.code, "| message:", err?.message);
    useAuthStore.getState().logout();

    if (err.code === "permission-denied") {
      useUserStore.getState().setUnauthorized();
    } else {
      useUserStore.getState().setUnauthorized();
    }
  }
};

// Redux thunk wrapper — keeps class components (this.props.dispatch(startUpdateUser())) working
export const startUpdateUser = () => {
  return async function (dispatch) {
    await updateUser();

    // Redux dual-write for class component subscribers
    const userState = useUserStore.getState().user;
    if (userState.isAuthenticated === "unauthorized") {
      dispatch({ type: "UNAUTHORIZED_USER" });
    } else if (userState.isAuthenticated) {
      const adminDataState = useAdminDataStore.getState();
      const groups = adminDataState.allGroups;
      const groupValues = Object.values(groups).map((g) => g.data);

      dispatch({
        type: "UPDATE_ADMIN_GROUPS",
        payload: { groups: groupValues },
      });
      dispatch({
        type: "UPDATE_ADMIN",
        payload: {
          profile: userState.profile,
          groups: userState.userRoles,
        },
      });
    }
  };
};

export const loadingUser = () => {
  useUserStore.getState().setLoading();
  // Return Redux action for class components using dispatch(loadingUser())
  return { type: "LOADING_USER" };
};

export const disableLoginFlow = () => {
  useUserStore.getState().setDisableLoginFlow();
  // Return Redux action for class components using dispatch(disableLoginFlow())
  return { type: "DISABLE_LOGIN_FLOW" };
};

export const logoutUser = () => {
  try {
    localStorage.clear();
    window.indexedDB.deleteDatabase("chant");
  } catch (e) {
    logger.error(e);
  }
  useAdminDataStore.getState().reset();
  useUserStore.getState().logout();
  // Return Redux action for class components using dispatch(logoutUser())
  return { type: "LOGOUT_USER" };
};
